"use client"

import Link from "next/link"
import { ArrowLeft, ImageIcon, MapPin, ShieldCheck } from "lucide-react"
import { PhotoSubmitForm } from "@/components/community/photo-submit-form"
import type { CommunityDiscoverySource } from "@/lib/community/discovery"

interface SubmitPageContentProps {
  initialGrassType: string | null
  initialSource?: CommunityDiscoverySource
}

export function SubmitPageContent({
  initialGrassType,
  initialSource,
}: SubmitPageContentProps) {
  return (
    <div className="min-h-screen bg-[#f4f1e8] py-6 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Link
          href="/community"
          className="mb-6 inline-flex min-h-11 items-center gap-1.5 rounded-full text-sm font-semibold text-green-950/70 hover:text-green-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to the gallery
        </Link>

        <div className="grid items-start gap-6 lg:grid-cols-[0.72fr_1.28fr] lg:gap-10">
          <aside className="overflow-hidden rounded-[2rem] bg-green-950 p-6 text-white lg:sticky lg:top-28 lg:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-green-300">Share your lawn</p>
            <h1 className="font-editorial mt-3 text-4xl font-semibold leading-none sm:text-5xl">
              Tell the story behind the lawn.
            </h1>
            <p className="mt-4 text-sm leading-6 text-green-50/75">
              A clear lawn photo, a title, and your ZIP is all it takes. Add the story behind it if you want. No account needed.
            </p>

            <ul className="mt-8 space-y-5">
              <li className="flex gap-3">
                <ImageIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-300" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold">What gets published</p>
                  <p className="mt-1 text-sm leading-5 text-green-50/70">Your photo, title, and broad region, plus any optional extras you add: lawn story, project timing, grass type, or social credit.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-green-300" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold">Your precise location stays private</p>
                  <p className="mt-1 text-sm leading-5 text-green-50/70">Only the first three ZIP digits are stored for regional browsing.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-300" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold">Reviewed before it goes live</p>
                  <p className="mt-1 text-sm leading-5 text-green-50/70">A moderator checks every submission. Email is optional and only used for status updates.</p>
                </div>
              </li>
            </ul>
          </aside>

          <PhotoSubmitForm
            initialGrassType={initialGrassType}
            initialSource={initialSource}
          />
        </div>
      </div>
    </div>
  )
}


